import {
    CUSTOMER_LEVEL,
    DiscountType,
    Promotion,
    PromotionType,
    SaleItem,
} from '@prisma/client';
import { MSProductVariantWithAttribute } from './query/product-query';

type PriceObject = {
    price: number; // discount amount (amount reduced). For DISCOUNT_PROMOTION we trust stored discountValue.
    percentage?: number; // % of normal price represented by discount (omitted for DISCOUNT_PROMOTION if already final value)
};

export type PriceType = {
    [K in keyof typeof CUSTOMER_LEVEL]?: PriceObject;
};

export type StepPriceValue = {
    step: number; // The step value for the promotion
    price: PriceType; // The price object for each customer level at this step
};

export type StepPriceType = {
    currentStep?: StepPriceValue; // The current step value for the promotion, if any
    nextStep?: StepPriceValue; // The next step value for the promotion, if any
    allSteps: StepPriceValue[]; // All available step tiers (sorted ascending by step)
};

export type BundleDealPriceType = {
    quantity: number; // The quantity required for the bundle deal
    price: PriceType; // The price object for each customer level at this quantity
    variantIds: string[]; // The variant IDs included in the bundle deal
};

export type PromotionDiscountValues = {
    [K in Exclude<PromotionType, 'STEP_PRICE' | 'BUNDLE_DEAL'>]?: {
        details: Promotion;
        value: PriceType;
    };
} & {
    // Holds the raw list of step price tiers. Selection (current/next) is computed separately via getBestPriceByType.
    STEP_PRICE?: { details: Promotion; value: StepPriceValue[] };
    BUNDLE_DEAL?: { details: Promotion; value: BundleDealPriceType };
};

// More user type please change this mapping
const rolePriceMapping = {
    GUEST: 'priceFull',
    BP: 'priceTrial',
} as const;

// Merchant special price per role. Used only as the base for STEP_PRICE so the
// step discount applies on top of an existing special price (when it is lower).
const compareRolePriceMapping = {
    GUEST: 'comparePriceFull',
    BP: 'comparePriceTrial',
} as const;

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export const checkPromotionTypeExists = (
    productVariant: MSProductVariantWithAttribute,
    type: PromotionType[] | PromotionType,
) => {
    const types = Array.isArray(type) ? type : [type];
    return (productVariant.promotionDiscounts ?? []).some((discount) =>
        // discount.promotion is included by our queries; add a light guard just in case
        discount?.promotion
            ? types.includes(discount.promotion.promotionType)
            : false,
    );
};

export const filterPromotionType = (
    productVariant: MSProductVariantWithAttribute,
    type: PromotionType[] | PromotionType,
) => {
    const types = Array.isArray(type) ? type : [type];
    return productVariant.promotionDiscounts?.filter((discount) =>
        // discount.promotion is included by our queries; add a light guard just in case
        discount?.promotion
            ? types.includes(discount.promotion.promotionType)
            : false,
    );
};

export const calculatePromotionDiscountByType = (
    discountType: DiscountType,
    discountValue: number,
    normalPrice?: number,
    promotionType?: PromotionType,
): PriceObject => {
    const base = normalPrice ?? 0;
    switch (discountType) {
        case 'PERCENTAGE': {
            if (promotionType === 'DISCOUNT_PROMOTION') {
                // discountValue already represents the discount amount; no percentage needed.
                const pct = base > 0 ? (discountValue / base) * 100 : 0;
                return {
                    price: round2(discountValue),
                    percentage: round2(pct),
                };
            }
            const discountAmount = base * (discountValue / 100);
            const pct = base > 0 ? (discountAmount / base) * 100 : 0;
            return {
                price: round2(base - discountAmount),
                percentage: round2(pct),
            };
        }
        case 'FIXED': {
            // Fixed final price => discount amount is fixedPrice
            const discountAmount = base > 0 ? Math.max(0, discountValue) : 0;
            const pct = base > 0 ? 100 - (discountAmount / base) * 100 : 0;
            return { price: round2(discountAmount), percentage: round2(pct) };
        }
        case 'FREE': {
            // Entire price reduced
            return { price: round2(base), percentage: base > 0 ? 100 : 0 };
        }
        case 'AMOUNT': {
            // discountValue is amount off
            const discountAmount = Math.min(discountValue, base);
            const pct = base > 0 ? (discountAmount / base) * 100 : 0;
            return {
                price: round2(base - discountAmount),
                percentage: round2(pct),
            };
        }
        default: {
            return { price: 0, percentage: 0 };
        }
    }
};

export const getPromotionDiscountedPrice = (
    productVariant: MSProductVariantWithAttribute,
): PromotionDiscountValues => {
    const getVariantPriceByRole = (
        roleKey: keyof typeof rolePriceMapping,
        preferSpecial = false,
    ): number => {
        const base = (productVariant as any)[rolePriceMapping[roleKey]] ?? 0;
        if (!preferSpecial) return base;
        // Use the merchant special price as the base when it is set and lower.
        const compare = (productVariant as any)[
            compareRolePriceMapping[roleKey]
        ];
        return compare != null && compare < base ? compare : base;
    };

    const buildRolePrices = (
        discountType: DiscountType,
        discountValue: number,
        promotionType?: PromotionType,
        preferSpecial = false,
    ): PriceType => {
        const res: PriceType = {};
        (
            Object.keys(rolePriceMapping) as Array<
                keyof typeof rolePriceMapping
            >
        ).forEach((role) => {
            const base = getVariantPriceByRole(role, preferSpecial);
            res[role] = calculatePromotionDiscountByType(
                discountType,
                discountValue,
                base,
                promotionType,
            );
        });
        return res;
    };

    const prices: Partial<
        Record<
            Exclude<PromotionType, 'STEP_PRICE' | 'BUNDLE_DEAL'>,
            { details: Promotion; value: PriceType }
        >
    > = {};
    const stepPrices: Array<{ step: number; price: PriceType }> = [];
    let stepPromotionDetails: Promotion | undefined;
    let bundleDeal:
        | { quantity: number; price: PriceType; variantIds: string[] }
        | undefined;
    let bundlePromotionDetails: Promotion | undefined;

    productVariant.promotionDiscounts?.forEach((discount) => {
        const pType = discount?.promotion?.promotionType;
        if (!pType) return;

        switch (pType) {
            case 'DISCOUNT_PROMOTION':
                prices.DISCOUNT_PROMOTION = {
                    details: discount.promotion,
                    value: buildRolePrices(
                        discount.discountType,
                        discount.discountValue ?? 0,
                        pType,
                    ),
                };
                return;

            case 'STEP_PRICE':
                const conditionStep = discount.promotionCondition;
                if (
                    conditionStep?.conditionType !== 'MIN_ORDER_AMOUNT' &&
                    conditionStep?.conditionType !== 'MIN_ORDER_QUANTITY'
                ) {
                    return;
                }
                if (!stepPromotionDetails)
                    stepPromotionDetails = discount.promotion;
                stepPrices.push({
                    step: conditionStep.conditionValue ?? 0,
                    price: buildRolePrices(
                        discount.discountType,
                        discount.discountValue ?? 0,
                        undefined,
                        true, // base step discount on the special price when present
                    ),
                });
                return;

            case 'BUNDLE_DEAL':
                const conditionBundle = discount.promotionCondition;
                const quantity =
                    conditionBundle?.conditionType === 'MIN_ORDER_AMOUNT'
                        ? conditionBundle?.conditionValue || 0
                        : 0;
                const productInclusions =
                    discount.promotion.promotionConditions?.find(
                        (cond) => cond.conditionType === 'PRODUCT_INCLUSION',
                    );
                bundleDeal = {
                    quantity,
                    price: buildRolePrices(
                        discount.discountType,
                        discount.discountValue ?? 0,
                    ),
                    variantIds: productInclusions?.msProductVariantIds || [],
                };
                if (!bundlePromotionDetails)
                    bundlePromotionDetails = discount.promotion;
                return;
        }
        const key = pType as Exclude<
            PromotionType,
            'STEP_PRICE' | 'BUNDLE_DEAL'
        >;
        const rolePrices = buildRolePrices(
            discount.discountType,
            discount.discountValue ?? 0,
        );
        const current = prices[key]?.value ?? {};
        const merged: PriceType = { ...current };
        (
            Object.keys(rolePriceMapping) as Array<
                keyof typeof rolePriceMapping
            >
        ).forEach((role) => {
            const curObj = current[role];
            const addObj = rolePrices[role];
            if (addObj) {
                if (!curObj) {
                    merged[role] = addObj;
                } else {
                    const base = getVariantPriceByRole(role);
                    const totalPrice = round2(
                        (curObj.price ?? 0) + (addObj.price ?? 0),
                    );
                    const pct =
                        base > 0
                            ? round2((totalPrice / base) * 100)
                            : undefined;
                    merged[role] = { price: totalPrice, percentage: pct };
                }
            }
        });
        prices[key] = { details: discount.promotion, value: merged };
    });

    const sortedStepPrices = stepPrices.sort((a, b) => a.step - b.step);

    const result: PromotionDiscountValues = { ...prices } as any;
    if (stepPrices.length > 0 && stepPromotionDetails)
        result.STEP_PRICE = {
            details: stepPromotionDetails,
            value: sortedStepPrices,
        };
    if (bundleDeal && bundlePromotionDetails)
        result.BUNDLE_DEAL = {
            details: bundlePromotionDetails,
            value: bundleDeal,
        };
    return result;
};

export function getBestPriceByType(
    prices: PriceType,
    promotionType: Exclude<PromotionType, 'STEP_PRICE' | 'BUNDLE_DEAL'>,
): PriceType;
export function getBestPriceByType(
    prices: Array<{ step: number; price: PriceType }>,
    promotionType: 'STEP_PRICE',
    numInCart: number,
): StepPriceType | null;
export function getBestPriceByType(
    prices: { quantity: number; price: PriceType; variantIds: string[] },
    promotionType: 'BUNDLE_DEAL',
    numInCart: undefined,
    cartItems: SaleItem[],
): BundleDealPriceType | null;
export function getBestPriceByType(
    prices:
        | PriceType
        | Array<{ step: number; price: PriceType }>
        | { quantity: number; price: PriceType; variantIds: string[] },
    promotionType: PromotionType,
    numInCart: number = 0, // Only used for STEP_PRICE
    cartItems: SaleItem[] = [], // Only used for BUNDLE_DEAL
): PriceType | StepPriceType | BundleDealPriceType | null {
    /**
     * 1. For DISCOUNT_PROMOTION (and other non step/bundle types) PriceObject.price represents the discount amount (higher is better).
     *  2. For other promotion types where price may represent a final price instead of discount amount, callers should adapt if needed.
     * The best price is determined by:
     * - For STEP_PRICE: the closest lesser or equal step to numInCart, or the last step if none match.
     * - For BUNDLE_DEAL: if items in cart match the variantIds which the total sum quantity matches the quantity in the promotion, return the price.
     * */

    switch (promotionType) {
        case 'DISCOUNT_PROMOTION':
        case 'SHIPPING_PROMOTION':
        case 'CART_DISCOUNT': {
            if (!prices || typeof prices !== 'object' || Array.isArray(prices))
                return null;
            return prices; // PriceType
        }
        case 'STEP_PRICE': {
            if (!Array.isArray(prices)) return null;
            if (prices.length === 0) return null;
            // Sort ascending by step value
            const sorted = [...prices].sort((a, b) => a.step - b.step);
            // Find currentStep: highest step <= numInCart
            const current = sorted.filter((s) => s.step <= numInCart).pop();
            if (!current) {
                // No eligible step yet -> nextStep is the first tier
                return {
                    currentStep: undefined,
                    nextStep: sorted[0],
                    allSteps: sorted,
                };
            }
            // Determine next step (first step strictly greater than current.step)
            const next = sorted.find((s) => s.step > current.step);
            if (!next) {
                // Already at or beyond max tier
                return {
                    currentStep: current,
                    nextStep: undefined,
                    allSteps: sorted,
                };
            }
            // Mid-tier: show current and upcoming
            return { currentStep: current, nextStep: next, allSteps: sorted };
        }
        case 'BUNDLE_DEAL': {
            if (Array.isArray(prices) || !prices) return null;
            const bundle = prices as {
                quantity: number;
                price: PriceType;
                variantIds: string[];
            };
            // Verify bundle eligibility: all variantIds present & summed quantity >= required quantity
            const requiredIds = bundle.variantIds || [];
            if (requiredIds.length === 0) return bundle; // treat as global bundle
            const quantities = cartItems
                .filter((ci) => requiredIds.includes(ci.msProductVariantId))
                .reduce((sum, ci) => sum + ci.quantity, 0);
            if (quantities >= bundle.quantity) {
                return bundle;
            }
            return null; // Not eligible yet
        }
        default:
            return null;
    }
}

/**
 * Compute final per-role price for a variant following promotion stacking rules:
 * 1. Discount Promotion OR Step Price (pick lower final price for each role – they don't stack with each other) Discount and Step Price won't be in the same variant anyway.
 * 2. (Updated) Bundle Deal is NO LONGER applied to the final price per request – we still surface eligibility info
 *    in the returned 'applied.bundleDeal' object for UI display, but it does not modify pricing.
 * Skips Shipping & Cart Discount per requirements.
 *
 * Assumptions about existing data structures (based on current implementation):
 * - DISCOUNT_PROMOTION PriceObject.price represents a discount amount to subtract from base price.
 * - STEP_PRICE / BUNDLE_DEAL PriceObject.price represent the final unit price when that promotion applies.
 */
export function computeFinalVariantPromotionPrice(
    variant: MSProductVariantWithAttribute,
    cartItems: SaleItem[] = [],
    quantityInCart: number = 0,
): {
    finalPrice: Record<string, number>;
    applied: {
        discountPromotion?: { details: Promotion; value: PriceType };
        stepPrice?: { details: Promotion; value: StepPriceType };
        bundleDeal?: { details: Promotion; value: BundleDealPriceType };
    };
} {
    const promotions = getPromotionDiscountedPrice(variant);

    const basePrices: Record<string, number> = {
        GUEST: (variant as any).priceFull ?? 0,
        BP: (variant as any).priceTrial ?? 0,
    };

    // 1. Pick between Discount Promotion vs Step Price (not both)
    const discountPromotion = promotions.DISCOUNT_PROMOTION; // {details,value}
    const stepPricesContainer = promotions.STEP_PRICE; // {details,value}
    const stepArray = stepPricesContainer?.value || [];
    const selectedStepValue = stepArray.length
        ? getBestPriceByType(stepArray, 'STEP_PRICE', quantityInCart)
        : null;

    const perRoleAfterFirst: Record<string, number> = { ...basePrices };
    const applied: {
        discountPromotion?: { details: Promotion; value: PriceType };
        stepPrice?: { details: Promotion; value: StepPriceType };
        bundleDeal?: { details: Promotion; value: BundleDealPriceType };
    } = {};

    (Object.keys(basePrices) as Array<keyof typeof basePrices>).forEach(
        (roleKey) => {
            const base = basePrices[roleKey];
            type RL = keyof PriceType;
            const role = roleKey as unknown as RL;
            let fromDiscount: number | undefined;
            const dp = discountPromotion?.value?.[role];
            if (dp) {
                // discountPromotion price = discount amount
                fromDiscount = Math.max(0, base - (dp.price || 0));
            }
            // Use currentStep price if eligible; otherwise fall back to the minimum (nextStep when currentStep undefined)
            const stepPriceObj =
                selectedStepValue?.currentStep ?? selectedStepValue?.nextStep;
            const fromStep = stepPriceObj?.price?.[role]?.price; // treated as candidate final price

            // Decide which applies (choose lower final price). If both undefined -> base.
            let chosen = base;
            if (fromDiscount !== undefined && fromStep !== undefined) {
                chosen = Math.min(fromDiscount, fromStep);
            } else if (fromDiscount !== undefined) {
                chosen = fromDiscount;
            } else if (fromStep !== undefined) {
                chosen = fromStep;
            }
            perRoleAfterFirst[roleKey] = chosen;
        },
    );

    // Mark which one actually applied globally (if at least one role benefited)
    const discountApplied = (
        Object.keys(basePrices) as Array<keyof typeof basePrices>
    ).some((rk) => {
        type RL = keyof PriceType;
        const role = rk as unknown as RL;
        const dp = discountPromotion?.value?.[role];
        if (!dp) return false;
        return perRoleAfterFirst[rk] < basePrices[rk];
    });
    const stepApplied = selectedStepValue
        ? (Object.keys(basePrices) as Array<keyof typeof basePrices>).some(
              (rk) => {
                  type RL = keyof PriceType;
                  const role = rk as unknown as RL;
                  const effectiveStep =
                      selectedStepValue.currentStep ??
                      selectedStepValue.nextStep; // fallback to minimum tier
                  const sp = effectiveStep?.price?.[role]?.price;
                  return sp !== undefined && perRoleAfterFirst[rk] === sp;
              },
          )
        : false;
    if (discountApplied && !stepApplied && discountPromotion)
        applied.discountPromotion = discountPromotion;
    if (
        stepApplied &&
        !discountApplied &&
        selectedStepValue &&
        stepPricesContainer
    )
        applied.stepPrice = {
            details: stepPricesContainer.details,
            value: selectedStepValue,
        };
    if (discountApplied && stepApplied) {
        // If both produced same minimum values for all roles we can prefer the one with greater overall reduction; keep both for transparency
        if (discountPromotion) applied.discountPromotion = discountPromotion;
        if (selectedStepValue && stepPricesContainer)
            applied.stepPrice = {
                details: stepPricesContainer.details,
                value: selectedStepValue,
            };
    }

    // 2. Bundle Deal eligibility (NO price modification):
    //    We detect eligibility but do not alter perRoleAfterFirst so that bundle deals are informational only.
    const bundleContainer = promotions.BUNDLE_DEAL;
    const bundleDeal = bundleContainer
        ? getBestPriceByType(
              bundleContainer.value,
              'BUNDLE_DEAL',
              undefined,
              cartItems,
          )
        : null;
    if (bundleDeal && bundleContainer) {
        applied.bundleDeal = {
            details: bundleContainer.details,
            value: bundleDeal,
        };
    }

    return { finalPrice: perRoleAfterFirst, applied };
}
